/**
 * The "Docs in chat" pill that opens and closes DocsInChatDrawer.
 *
 * Rendered by both the owner's chat top bar (src/pages/Navs/topnav) and the
 * shared chat page header (src/pages/secured-share-page/SharedChatHeader), so
 * the button, its count and its analytics stay identical on both surfaces.
 * Renders nothing when the conversation has no documents or attachments.
 */
import { FileText } from "lucide-react";
import { useDispatch } from "react-redux";
import { setDocsInChatOpen } from "src/store/slices/modalSlice";
import { useAppSelector } from "src/hooks/useAppSelector";
import useDocsInChatCount from "src/hooks/useDocsInChatCount";
import { trackAnalytics } from "src/pages/helpers";
import { BUTTON_ROLES, BUTTON_TYPES, EVENT_NAMES, EVENT_PARAMETERS, MODAL_NAMES } from "src/utils/EventConstants";

interface DocsInChatToggleProps {
  /** `desktop` is the full "Docs in chat" pill (md and up); `mobile` is the compact "Docs" pill (below md). */
  variant: "desktop" | "mobile";
  /** Analytics page_name of the surface rendering the toggle. */
  pageName: string;
}

const DocsInChatToggle = ({ variant, pageName }: DocsInChatToggleProps) => {
  const dispatch = useDispatch();
  const docsInChatOpen = useAppSelector((state) => state.modalSlice.docsInChatOpen);
  const newDocArrived = useAppSelector((state) => (state.messageSlice as any).newDocArrived);
  const threadId = useAppSelector((state) => (state.messageSlice as any).threadData?.id);
  const userMetadata = useAppSelector((state) => (state.onboarding as any).userMetadata);
  const totalDocCount = useDocsInChatCount();

  if (totalDocCount <= 0) return null;

  const buttonText = variant === "desktop" ? "Docs in chat" : "Docs";

  const handleClick = () => {
    trackAnalytics(
      EVENT_NAMES.CLICKED_BUTTON,
      {
        [EVENT_PARAMETERS.PAGE_NAME]: pageName,
        [EVENT_PARAMETERS.THREAD_ID]: threadId,
        [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
        [EVENT_PARAMETERS.BUTTON_TEXT]: buttonText,
        [EVENT_PARAMETERS.BUTTON_ROLE]: docsInChatOpen
          ? BUTTON_ROLES.CLOSE_DOCS_IN_CHAT
          : BUTTON_ROLES.OPEN_DOCS_IN_CHAT,
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
        [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.TOP_NAV,
      },
      userMetadata,
    );
    dispatch(setDocsInChatOpen(!docsInChatOpen));
  };

  if (variant === "mobile") {
    return (
      <button
        data-testid="docs-in-chat-toggle-mobile"
        onClick={handleClick}
        aria-pressed={docsInChatOpen}
        className="md:hidden flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] font-medium border bg-[#EFE3D3] text-[#242424] border-[#EFE3D3] transition-colors"
      >
        <FileText size={16} strokeWidth={1.5} />
        Docs
        <span
          data-testid="docs-in-chat-count-mobile"
          className="hidden min-[350px]:flex items-center justify-center w-[22px] h-[22px] text-[10px] rounded-full font-semibold bg-white text-[#242424]"
          style={{ border: "0.69px solid #E5E7EB" }}
        >
          {totalDocCount}
        </span>
      </button>
    );
  }

  return (
    <button
      data-testid="docs-in-chat-toggle-desktop"
      onClick={handleClick}
      aria-pressed={docsInChatOpen}
      className={`hidden md:flex items-center gap-1.5 px-2 py-1.5 rounded-full text-[12px] font-medium border transition-colors ${
        docsInChatOpen
          ? "bg-[#1B2B48] text-white border-[#1B2B48]"
          : "bg-[#EFE3D3] text-[#242424] border-[#EFE3D3] hover:bg-[#E5D9C7]"
      }`}
    >
      <FileText size={16} strokeWidth={1.5} />
      Docs in chat
      {newDocArrived && !docsInChatOpen && (
        <span data-testid="docs-in-chat-new-doc-dot" className="w-[6px] h-[6px] rounded-full bg-[#1f4fd1] animate-pulse" />
      )}
      <span
        data-testid="docs-in-chat-count-desktop"
        className={`flex items-center justify-center w-[22px] h-[22px] text-[10px] rounded-full font-semibold ${
          docsInChatOpen ? "bg-[#2A3D5B] text-white" : "bg-white text-[#242424]"
        }`}
        style={{ border: docsInChatOpen ? "none" : "0.69px solid #E5E7EB" }}
      >
        {totalDocCount}
      </span>
    </button>
  );
};

export default DocsInChatToggle;
